/**
 * Correlation-id state shared by the telemetry client and the fetch wrapper.
 *
 * The pattern:
 *   - One id per user action (click "Send", click "Run skill", open a panel).
 *   - That same id flows on the `X-Correlation-Id` header of every backend
 *     call made while the action is in flight, and on every telemetry event
 *     emitted during the action.
 *   - Server-side `CorrelationIdMiddleware` echoes the id into Serilog's
 *     `LogContext`, so the same value lands in `AivaStudioLogs_CL`,
 *     `AivaStudioAddinLogs_CL`, and the SQL audit table — one KQL query
 *     joins all three.
 *
 * Implementation notes:
 *   - Module-scoped mutable state on purpose. The browser is single-threaded
 *     and there is never more than one "current" user action at a time.
 *   - `withCorrelationId` is the canonical way to scope an action. Callers
 *     should prefer it over manual `set`/`clear` so they can't forget to
 *     clear (which would leak the id into the next, unrelated action).
 */

let current: string | undefined;

/**
 * Generate a fresh correlation id. Uses `crypto.randomUUID()` when available
 * (every supported browser and Node ≥ 14.17), and falls back to a UUID-v4
 * synthesised from `crypto.getRandomValues()` bytes for the rare runtime
 * that ships Web Crypto but not `randomUUID()`.
 *
 * Both paths are cryptographically secure. Sonar typescript:S2245 flagged a
 * previous Math.random-based fallback as a weak PRNG; this implementation
 * uses only Web Crypto and throws if neither API is available.
 */
export function generateCorrelationId(): string {
  return generateUuidV4();
}

/**
 * Internal — synthesise a RFC 4122 v4 UUID using Web Crypto. Exported so the
 * telemetry client can reuse the same generator for session/event ids
 * without duplicating the fallback logic.
 */
export function generateUuidV4(): string {
  // Fast path — every modern browser and Node ≥ 14.17 ships this.
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }

  // Fallback — manual UUID-v4 from cryptographically-secure random bytes.
  // Only exercised on legacy webviews that have Web Crypto but not
  // randomUUID(). Math.random is intentionally NOT used here because Sonar
  // (rightly) flags it as a weak PRNG for security-adjacent values.
  if (typeof globalThis.crypto?.getRandomValues === "function") {
    const bytes = new Uint8Array(16);
    globalThis.crypto.getRandomValues(bytes);
    // RFC 4122 §4.4: set the version (top 4 bits of byte 6 = 0b0100) and
    // the variant (top 2 bits of byte 8 = 0b10).
    // Nullish-coalesce because `noUncheckedIndexedAccess` types
    // `Uint8Array[index]` as `number | undefined`; we know the indices are
    // in-bounds for a freshly-allocated 16-byte buffer, but the type system
    // can't see that without an assertion.
    bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
    bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  // No source of cryptographic randomness — fail loudly rather than
  // silently degrading to a predictable Math.random sequence.
  throw new Error(
    "generateUuidV4: Web Crypto API is not available; cannot produce a secure UUID.",
  );
}

/** Return the currently-active correlation id, or `undefined` if none. */
export function getCurrentCorrelationId(): string | undefined {
  return current;
}

/** Set the active correlation id. Prefer {@link withCorrelationId} where you can. */
export function setCurrentCorrelationId(id: string | undefined): void {
  current = id;
}

/** Clear the active correlation id. Synonym for `setCurrentCorrelationId(undefined)`. */
export function clearCurrentCorrelationId(): void {
  current = undefined;
}

/**
 * Run a function with a freshly-generated correlation id active. The id is
 * cleared (or restored to whatever was previously active) when the callback
 * resolves or rejects.
 *
 * Use this to wrap a user-action handler: every `fetch` and every
 * `telemetryClient.enqueue(...)` call made while the action is in flight
 * automatically picks up the same id.
 */
export async function withCorrelationId<T>(
  fn: (correlationId: string) => Promise<T> | T,
): Promise<T> {
  const previous = current;
  const id = generateCorrelationId();
  current = id;
  try {
    return await fn(id);
  } finally {
    current = previous;
  }
}
